/**
 * useIsMobile — detects mobile viewport / touch-primary devices
 *
 * Uses a combination of:
 * 1. matchMedia for screen width (≤768px = mobile, ≤1024px = tablet)
 * 2. Touch-primary pointer check (coarse pointer = finger)
 * 3. UA sniffing as fallback for edge cases (iPadOS, etc.)
 *
 * Returns { isMobile, isTablet, isTouchDevice }
 * - isMobile: phone-sized viewport OR touch-only with narrow screen
 * - isTablet: medium viewport with touch (iPad etc.)
 * - isTouchDevice: any device where primary input is touch
 */

import { useState, useEffect } from 'react';

interface MobileState {
  isMobile: boolean;
  isTablet: boolean;
  isTouchDevice: boolean;
}

function detect(): MobileState {
  if (typeof window === 'undefined') {
    return { isMobile: false, isTablet: false, isTouchDevice: false };
  }

  const width = window.innerWidth;
  const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;

  // UA fallback for iPadOS (reports as Mac but has touch)
  const ua = navigator.userAgent || '';
  const isIOSUa = /iPad|iPhone|iPod/i.test(ua);
  const isIPadOs =
    navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
  const isAndroid = /Android/i.test(ua);

  const isTouchDevice = isCoarsePointer || isIOSUa || isIPadOs || isAndroid;
  const isMobile = width <= 768 || (isTouchDevice && width <= 768);
  const isTablet =
    !isMobile && (width <= 1024 || isIPadOs) && isTouchDevice;

  return { isMobile, isTablet, isTouchDevice };
}

export function useIsMobile(): MobileState {
  const [state, setState] = useState<MobileState>(detect);

  useEffect(() => {
    const handleResize = () => {
      setState(detect());
    };

    window.addEventListener('resize', handleResize);
    // Also detect orientation changes on mobile
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return state;
}

/**
 * Standalone function (non-hook) for use outside React components.
 */
export function isMobileDevice(): boolean {
  return detect().isMobile;
}
